// FAQ media loader — authenticated fetch of the auth-protected FAQ assets
// (TP_FAQ_BRIEF §5: images "served with the same auth protection as the
// page (never public)").
//
// The backend endpoint (dashboard/faq_assets.py, feature/faq-asset-protection)
// serves the pipeline's screenshots + marker JSONs at
//   GET /dashboard/faq-assets/faq/<tier>/<category>/<key>.<desktop|mobile>.<png|json>
// behind IsAuthenticated. A plain <img src> cannot carry the Bearer token, so
// the slot fetches the bytes with auth and renders a blob: object URL.
//
// Own axios instance, isolated from the shared services/api.js — mirrors
// services/agentRadarService.js (same Bearer attachment, small PR surface).
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;

const authApi = axios.create({ baseURL: API_BASE_URL, withCredentials: true });

authApi.interceptors.request.use((config) => {
  try {
    const tokens = JSON.parse(localStorage.getItem("tokens") || "null");
    if (tokens?.access) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${tokens.access}`;
    }
  } catch (e) {
    /* no token — request proceeds and the server returns 401 */
  }
  return config;
});

// Blob URLs per asset path — an accordion open/close must not refetch, and
// revoking on unmount would break a second slot showing the same image.
const objectUrlCache = new Map();
// Negative cache: a 404 means "not rendered yet" (placeholder state) — do
// not hammer the API every re-render.
const missing = new Set();

/**
 * Resolve one protected FAQ image to a blob: URL, or null while the asset
 * does not exist yet (the slot then keeps its placeholder state).
 * @param {string} assetPath e.g. "faq/1a/<category>/<key>.desktop.png"
 */
export async function fetchFaqAssetUrl(assetPath) {
  if (objectUrlCache.has(assetPath)) return objectUrlCache.get(assetPath);
  if (missing.has(assetPath)) return null;
  try {
    const res = await authApi.get(`/dashboard/faq-assets/${assetPath}`, {
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    objectUrlCache.set(assetPath, url);
    return url;
  } catch (error) {
    if (error?.response?.status === 404) missing.add(assetPath);
    return null;
  }
}

/** Marker JSON companion of an image ("…png" → "…json"), null when absent. */
export async function fetchFaqMarkers(assetPath) {
  const jsonPath = assetPath.replace(/\.png$/, ".json");
  if (missing.has(jsonPath)) return null;
  try {
    const res = await authApi.get(`/dashboard/faq-assets/${jsonPath}`);
    return res.data;
  } catch (error) {
    if (error?.response?.status === 404) missing.add(jsonPath);
    return null;
  }
}

// Test hook — lets Jest reset module state between cases.
export function __resetFaqAssetCaches() {
  objectUrlCache.clear();
  missing.clear();
}
